import type { ApiError, ApiResult } from '@shared/types';

/**
 * 平台 API 客户端。
 *
 * 约定：Worker 永远返回 `{ ok: true, data }` 或 `{ ok: false, error }`，
 * 因此这里把错误统一抛成 `ApiError`（带 code / message / details），
 * 业务代码只需 try/catch 或交给 React Query 之类的边界处理。
 */

export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(status: number, error: ApiError) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = error.code;
    this.details = error.details;
  }

  /** 422 校验失败时，把 `{field, message}` 列表转成表单可直接用的映射。 */
  get fieldErrors(): Record<string, string> {
    if (!Array.isArray(this.details)) return {};
    const record: Record<string, string> = {};
    for (const item of this.details) {
      if (item && typeof item === 'object' && 'field' in item && 'message' in item) {
        const { field, message } = item as { field: unknown; message: unknown };
        if (typeof field === 'string' && typeof message === 'string') record[field] = message;
      }
    }
    return record;
  }
}

/**
 * 后端基址。
 *
 * 开发环境留空，由 Vite 代理 `/api` 到本地 Worker；生产环境通过
 * `VITE_API_BASE` 指向 API Worker（本项目是 https://api.havorix.work）。
 * 因此前端代码里所有平台请求都必须走本模块，不要直接 `fetch('/api/...')`，
 * 否则在生产会打到静态托管上（Pages 对 POST 返回 405）。
 */
const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const combined = `${API_BASE}${path}`;
  const url = API_BASE ? new URL(combined) : new URL(combined, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, String(value));
    }
  }
  // 同源请求返回相对路径，让 Pages 与 Worker 的部署方式解耦；
  // 显式配置了 VITE_API_BASE 时按绝对地址请求（例如前端与 Worker 不同域）。
  return API_BASE ? url.toString() : `${url.pathname}${url.search}`;
}

let unauthorizedHandler: (() => void) | null = null;

/** 注册全局 401 回调：会话失效时立刻把界面切回登录页。 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      credentials: 'include',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiRequestError(0, {
      code: 'network_error',
      message: '无法连接到平台服务，请检查网络或后端是否已启动',
    });
  }

  const text = await response.text();
  if (response.status === 401) unauthorizedHandler?.();
  if (text.trim() === '') {
    if (response.ok) return undefined as T;
    throw new ApiRequestError(response.status, {
      code: 'empty_response',
      message: `服务返回了空响应（HTTP ${response.status}）`,
    });
  }

  let parsed: ApiResult<T>;
  try {
    parsed = JSON.parse(text) as ApiResult<T>;
  } catch {
    throw new ApiRequestError(response.status, {
      code: 'invalid_response',
      message: `服务返回了非 JSON 响应（HTTP ${response.status}）`,
    });
  }

  if (!parsed.ok) throw new ApiRequestError(response.status, parsed.error);
  return parsed.data;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};

/** 把任意异常转成可直接展示给用户的中文文案。 */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message;
  if (error instanceof Error) return error.message;
  return '发生未知错误';
}
